<script lang="ts">
import DashboardLayout from '../DashboardLayout.svelte';
import DashHero from '../shared/DashHero.svelte';
import BedMapWidget from '../widgets/BedMapWidget.svelte';
import ActiveIngresosQueue from '../widgets/ActiveIngresosQueue.svelte';
import CitasHoyQueue from '../widgets/CitasHoyQueue.svelte';
import LabQueueWidget from '../widgets/LabQueueWidget.svelte';
import KardexQueueWidget from '../widgets/KardexQueueWidget.svelte';
import ImagenQueueWidget from '../widgets/ImagenQueueWidget.svelte';
import QuirofanoAgendaWidget from '../widgets/QuirofanoAgendaWidget.svelte';
import OpsTimeline from '../widgets/OpsTimeline.svelte';
import QuickActions from '../widgets/QuickActions.svelte';
import AlertBanner, { type AlertItem } from '../widgets/AlertBanner.svelte';
import AltasPendientesWidget from '../widgets/AltasPendientesWidget.svelte';
import KpiCard from '../widgets/KpiCard.svelte';
import { Dashboard, Cuentas, Laboratorio, Imagenologia } from '../../../lib/api';
import type { SemaforoData } from '../widgets/BedSemaphoreGrid.svelte';
import type { DashboardResumen, Cuenta } from '../../../lib/api';

  let { onNavigate }: { onNavigate: (tab: string) => void } = $props();

  let resumen = $state<DashboardResumen | null>(null);
  let semaforo = $state<SemaforoData | null>(null);
  let cuentasAbiertas = $state(0);
  let facturadoMes = $state(0);
  let labPendientes = $state(0);
  let imgPendientes = $state(0);
  let alerts = $state<AlertItem[]>([]);
  let error = $state<string | null>(null);

  function fmtQ(n: number) {
    return new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ', maximumFractionDigits: 0 }).format(n);
  }

  async function cargar() {
    error = null;
    try {
      const [r, cuentas, solLab, procLab, solImg] = await Promise.all([
        Dashboard.resumen(),
        Cuentas.list().catch(() => [] as Cuenta[]),
        Laboratorio.ordenes('SOLICITADA').catch(() => []),
        Laboratorio.ordenes('EN_PROCESO').catch(() => []),
        Imagenologia.ordenes('SOLICITADA').catch(() => []),
      ]);
      resumen = r;
      semaforo = r.semaforo;
      labPendientes = solLab.length + procLab.length;
      imgPendientes = solImg.length;

      const abiertas = cuentas.filter(c => c.estado === 'ABIERTA' || !c.estado);
      cuentasAbiertas = abiertas.length;
      facturadoMes = cuentas.reduce((s, c) => s + (c.total ?? 0), 0);

      const mant = (r.semaforo?.porColor?.gris ?? 0) + (r.camasMantenimiento ?? 0);
      const nuevasAlertas: AlertItem[] = [];
      if (mant > 0) {
        nuevasAlertas.push({
          level: 'warn',
          title: `${mant} camas en mantenimiento o bloqueadas`,
          detail: 'Revise el semáforo antes de nuevos ingresos.',
        });
      }
      if (r.urgenciasActivas && r.urgenciasActivas > 0) {
        nuevasAlertas.push({
          level: 'critical',
          title: `${r.urgenciasActivas} pacientes en urgencias`,
          detail: 'Capacidad crítica en ER/URG.',
        });
      }
      if (labPendientes > 10) {
        nuevasAlertas.push({
          level: 'warn',
          title: `${labPendientes} órdenes lab pendientes`,
          detail: 'Backlog de validación LIS.',
        });
      }
      if (cuentasAbiertas > 15) {
        nuevasAlertas.push({
          level: 'info',
          title: `${cuentasAbiertas} cuentas abiertas`,
          detail: 'Revise facturación y cierres pendientes.',
        });
      }
      alerts = nuevasAlertas;
    } catch (e: any) {
      error = e?.message ?? String(e);
    }
  }

  const quickActions = [
    { id: 'semaforo', label: 'Semáforo', icon: '🛏️' },
    { id: 'ingresos', label: 'Ingresos', icon: '📥' },
    { id: 'cuentas', label: 'Cobrar', icon: '💳' },
    { id: 'configuracion', label: 'Marca y firmas', icon: '🎨' },
    { id: 'farmaciaPos', label: 'Recetas POS', icon: '℞' },
    { id: 'usuarios', label: 'Usuarios', icon: '👥' },
    { id: 'reportes', label: 'Reportes MSPAS', icon: '📈' },
    { id: 'laboratorio', label: 'Laboratorio', icon: '🧪' },
    { id: 'quirofano', label: 'Quirófano', icon: '🔪' },
    { id: 'altas', label: 'Altas', icon: '🚪' },
  ];

  $effect(() => { cargar(); const t = setInterval(cargar, 30000); return () => clearInterval(t); });
</script>

<DashboardLayout variant="admin">
  {#snippet actions()}
    <button type="button" class="ghost" onclick={cargar}>↻ Recargar</button>
  {/snippet}

  <DashHero
    emoji="🏥"
    title="Centro de comando"
    subtitle="Ocupación, ingresos, colas clínicas y facturación"
    variant="admin"
  />

  {#if error}<p class="err">{error}</p>{/if}

  <AlertBanner {alerts} />

  <QuickActions actions={quickActions} onAction={onNavigate} />

  <div class="dash-kpis">
    <KpiCard label="Ocupación" value="{semaforo?.tasaOcupacionPct ?? 0}%" hint="{semaforo?.ocupadas ?? 0} / {semaforo?.totalCamas ?? 0} camas" tone="total" />
    <KpiCard label="Facturado" value={fmtQ(facturadoMes)} hint="{cuentasAbiertas} cuentas abiertas" tone="brand" />
    <KpiCard label="Cirugías hoy" value={resumen?.cirugiasHoy ?? 0} hint="Block quirúrgico" tone="neutral" />
    <KpiCard label="Urgencias" value={resumen?.urgenciasActivas ?? 0} hint="ER / URG activos" tone="danger" />
  </div>

  <div class="dash-grid">
    <div class="col-12">
      <BedMapWidget camas={resumen?.camas} onCamaClick={() => onNavigate('semaforo')} />
    </div>

    <div class="col-6"><ActiveIngresosQueue limit={6} /></div>
    <div class="col-6"><AltasPendientesWidget limit={6} {onNavigate} /></div>

    <div class="col-4"><LabQueueWidget limit={6} interactive /></div>
    <div class="col-4"><ImagenQueueWidget limit={6} {onNavigate} /></div>
    <div class="col-4"><KardexQueueWidget limit={6} /></div>

    <div class="col-6"><CitasHoyQueue limit={6} interactive /></div>
    <div class="col-6"><QuirofanoAgendaWidget limit={6} {onNavigate} /></div>

    <div class="col-12"><OpsTimeline limit={10} /></div>
  </div>
</DashboardLayout>

<style>
  .err { color: var(--danger); font-size: 0.88rem; }
  .ghost { padding: 0.4rem 0.75rem; border-radius: 8px; font: inherit; font-size: 0.82rem; cursor: pointer; border: 1px solid var(--border); background: var(--bg-surface); color: var(--text-secondary); }
</style>
